import { datadogLogs } from '@datadog/browser-logs';
import type { AppSessionData } from '@strength/server-auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { twAuthClient } from '@teamworksdev/web-sdk/auth';
import axios from 'axios';
import { z } from 'zod';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { oktaAuth } from './app/okta/auth';
import { closeActiveZero } from './app/zero/zero-active-instance';
import { environment } from './environment';
import { clearReportingUser } from './reporting/reporting';

export type AuthData = AppSessionData;

export interface AuthContext {
  setAuth: (auth: AuthData | null) => void;
  auth: AuthData | null;
}

export const SESSION_QUERY_KEY = ['session'];

const AuthContext = createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const result = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchAuthData,
  });

  // This will refresh the session every 5 minutes
  useQuery({
    queryKey: ['session-ping'],
    queryFn: () =>
      fetch(`${environment.VITE_API_URL}/web-session-ping`, {
        method: 'POST',
        credentials: 'include',
      }),
    refetchInterval: 1000 * 60 * 5,
    enabled: !!result.data,
  });

  const queryClient = useQueryClient();
  const setAuth = useCallback(
    (auth: AuthData | null): void => {
      queryClient.setQueryData(SESSION_QUERY_KEY, auth);
    },
    [queryClient],
  );
  const auth = result.data ?? null;
  const value = useMemo(() => ({ setAuth, auth }), [setAuth, auth]);

  // Auth:
  // We only use Okta to authenticate a user on their login directly to S&C.
  // When we app switch we trust that the axle token is valid and set a backend session for the user.
  // Meaning a user's okta session can be expired as long as their backend session is valid.
  // If a user has a valid okta session but their backend session is expired then we can attempt to re-authenticate the user
  // However, if the user's backend session is expired without a valid okta session we need to redirect to the /login page (this page will handle pushing them to the okta sign in).
  // In app.tsx, if trpc routes throw a 401 we will set the auth to null.

  // Handle Expired Sessions:
  // This useEffect handles the cleanup when auth becomes null (e.g., from 401 error)
  // We only want to clean up when auth is null AND NOT on a /login route. Auth can be null on these routes as we attempt to authenticate users.
  // If auth is null at any other point in the app we essentially want to send the user back to the okta sign-in which more or less requires logging the user out.
  useEffect(() => {
    async function handleExpiredSession() {
      // explicitly check for a backend session before cleanup
      try {
        const backendAuth = await fetchAuthData();
        if (backendAuth) {
          setAuth(backendAuth);
          return;
        }
      } catch {
        // No backend session, continue with cleanup
        datadogLogs.logger.info(
          'No backend session, continuing with handleExpiredSession',
        );
      }
      await cleanupSessions(queryClient);
      window.location.href = '/login';
    }

    const path = window.location.pathname;
    const isLoginPath = path.startsWith('/login') || path === '/';

    // Only act if auth is null, we're not on login page, and query has loaded
    if (!auth && result.data !== undefined && !isLoginPath) {
      handleExpiredSession();
    }
  }, [auth, result.data, queryClient]);

  // initialize tw auth client to use core modules
  useEffect(() => {
    if (auth?.type === 'org') {
      twAuthClient.init({
        fetchToken: () => fetchUnityWToken(auth.profileId),
      });
    }
  }, [auth]);

  if (result.data !== undefined) {
    return (
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
  } else if (result.error) {
    // TODO: Universal error screen, maybe throw an error here and catch in an error boundary?
    datadogLogs.logger.error('Error fetching auth data', {
      error: result.error,
    });
    return <div>Something went wrong...</div>;
  } else {
    // TODO: Universal loading screen
    datadogLogs.logger.info('No auth result, showing universal loading screen');
    return null;
  }
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useAuthDataOrThrow() {
  const auth = useContext(AuthContext)?.auth;
  if (!auth) {
    throw new Error('No auth data found');
  }
  return auth;
}

export function useOrgAuthDataOrThrow() {
  const authData = useAuthDataOrThrow();
  if (authData.type !== 'org') {
    throw new Error('No org auth data found');
  }
  return authData;
}

export function useAdminAuthDataOrThrow() {
  const authData = useAuthDataOrThrow();
  if (authData.type !== 'admin') {
    throw new Error('No org auth data found');
  }
  return authData;
}

// TWAuthClient polls this on a setInterval it doesn't await/catch, so a thrown
// error here becomes an unhandled rejection in the browser; swallow and return
// null instead (a valid "no token yet" value per its FetchTokenFn type).
export async function fetchUnityWToken(
  strengthProfileId: string,
): Promise<string | null> {
  try {
    const res = await axios.post(`${environment.VITE_API_URL}/unity-w-token`, {
      strengthProfileId,
    });
    return res.data.token;
  } catch (error) {
    datadogLogs.logger.error('Error fetching unity w-token:', { error });
    return null;
  }
}

// This obviously doesn't work with offline mode
export async function fetchAuthData(): Promise<AuthData | null> {
  const res = await fetch(`${environment.VITE_API_URL}/web-session-check`, {
    credentials: 'include',
  });
  if (res.status === 200) {
    return res.json();
  } else if (res.status === 401) {
    datadogLogs.logger.info('Unable to fetch auth data, returning null');
    return null;
  } else {
    throw new Error('Unexpected status code');
  }
}

export async function loginWithOktaToken(
  idToken: string,
  onSuccess: (auth: AuthData) => void,
) {
  const res = await axios.post(
    `${environment.VITE_API_URL}/web-login-with-okta`,
    { idToken },
    { withCredentials: true },
  );
  onSuccess(res.data as AuthData);
}

const axleTokenPayloadSchema = z.object({ strength_profile_id: z.string() });

export async function loginWithAxleToken(
  token: string,
  onSuccess: (auth: AuthData) => void,
): Promise<void> {
  const verifyRes = await axios.post(
    `${environment.VITE_API_URL}/verify-axle-token`,
    { token },
  );
  const { strength_profile_id: profileId } = axleTokenPayloadSchema.parse(
    verifyRes.data,
  );
  const loginRes = await axios.post(
    `${environment.VITE_API_URL}/web-login-with-axle-redirect`,
    { profileId },
    { withCredentials: true },
  );
  onSuccess(loginRes.data as AuthData);
}

export async function loginWithOktaBypass(): Promise<AuthData | null> {
  try {
    const res = await axios.post(
      `${environment.VITE_API_URL}/web-dev-login`,
      undefined,
      { withCredentials: true, validateStatus: () => true },
    );
    if (res.status === 200) {
      return res.data as AuthData;
    }
    return null;
  } catch {
    return null;
  }
}

async function cleanupSessions(queryClient: ReturnType<typeof useQueryClient>) {
  // Stop the active Zero instance from syncing, but keep its IndexedDB cache so
  // the next login rehydrates from the warm cache instead of a full re-sync.
  await closeActiveZero();
  try {
    await fetch(`${environment.VITE_API_URL}/web-logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    datadogLogs.logger.error('Error during backend logout:', { error });
  }
  queryClient.clear();
  // this will redirect to the okta sign in
  try {
    await oktaAuth.signOut({ clearTokensBeforeRedirect: true });
  } catch (error) {
    // Pass the error via Datadog's dedicated third argument, not nested in
    // context — only that populates LogsEvent.error.kind/message, which is
    // what isTransientAuthNetworkLogError filters on (SC-4259).
    datadogLogs.logger.error(
      'Error signing out of Okta:',
      undefined,
      error instanceof Error ? error : new Error(String(error)),
    );
  }
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useCallback(
    async function logout() {
      await cleanupSessions(queryClient);
    },
    [queryClient],
  );
}

export type SessionSwitchData =
  | {
      admin: true;
    }
  | {
      admin: false;
      profileId: string;
    };

export function useSessionSwitch() {
  return useCallback(async function sessionSwitch(data: SessionSwitchData) {
    await axios.post(`${environment.VITE_API_URL}/web-session-switch`, data, {
      withCredentials: true,
    });

    // reset gainsight and customer.io
    await clearReportingUser();
    twAuthClient.refreshToken();

    // Could probably get this working without a reload, but this is easier for now
    window.location.href = '/';
  }, []);
}
